import { Value } from '@philips-internal/oswald-ui-core';
import { defaultIfUndefined } from './object-utils';

/**
 * rawValue may or may not have a value.
 * The rawValue will receive some value of type {@link Value}, except object.
 */
type FieldValue<T> = T & {
  rawValue?: Value;
}

export interface Field<T> {
  newValue: FieldValue<T>;
  oldValue: FieldValue<T> | '';
  propertyName: string;
}

interface Row {
  key: string;
  value: Value;
}

export class ObjectComparatorUtils {

  /**
   * @param changedObj The object with the user's changes.
   * @param originalObj The initial object, as it came from the database
   * @returns An object with propertyName, oldValue, and newValue.
   */
  static getChangedFields<T extends Record<string, Value>>(changedObj: T, originalObj: T): Field<T>[] {
    if (!changedObj || !originalObj) {
      throw Error('Invalid paramters');
    }

    const changedFields: Field<T>[] = [];
    Object.entries(changedObj).forEach(([key, value]) => {
      const originalObjectRow = ObjectComparatorUtils.findRowInObjectByPropertyName(originalObj, key);
      const origObjRowValue = defaultIfUndefined(originalObjectRow?.value, '');
      if (origObjRowValue !== value) {
        changedFields.push({
          newValue: ObjectComparatorUtils.getFieldValue(value),
          oldValue: ObjectComparatorUtils.getFieldValue(origObjRowValue),
          propertyName: key
        });
      }
    });

    return changedFields;
  }

  /**
   * Searches in the informed object for the line that has the specified property.
   * @param obj Any object
   * @param propertyName Object property
   * @returns A new object with only the found row.
   */
  private static findRowInObjectByPropertyName<T extends Record<string, Value>>(obj: T, propertyName: string): Row | undefined {
    const entriesFound = Object.entries(obj).find(([key]) => key === propertyName);

    if (!entriesFound) {
      return entriesFound;
    }

    return {
      key: entriesFound[0],
      value: entriesFound[1]
    }
  }

  /**
   * rawValue may or may not have a value.
   * The rawValue will receive some value of type {@link Value}, except object.
   * @param value An object or primitive type
   * @returns FieldValue<T>
   */
  private static getFieldValue<T>(value: Value): FieldValue<T> {
    if (ObjectComparatorUtils.isObject(value)) {
      return value as FieldValue<T>;
    }

    return {
      rawValue: value
    } as FieldValue<T>;
  }

  static isObject<T>(value: T): boolean {
    return typeof value === 'object';
  }
}